import { lazy } from "react";

import OncologyResultData from "../Pages/Requisition/OncologyResultData";
import UserListContextProvider from "Pages/Admin/UserManagement/UserListContext";
import AuthenticatorWrapper from "Pages/Auth/AuthenticatorWrapper";
import ConsentUpdate from "Pages/Auth/ConsentUpdate";
import ForgetPassword from "Pages/Auth/ForgetPassword/ForgetPassword";
import AuthenticateWithCode from "Pages/Auth/MFA/AuthenticateWithCode";
import AuthenticatorVerificationCode from "Pages/Auth/MFA/AuthenticatorVerificationCode";
import VerificationCode from "Pages/Auth/MFA/VerificationCode";
import NewPassword from "Pages/Auth/NewPassword";
import PatientDataForm from "Pages/Auth/PatientDataForm";
import SecurityQuestionsUserName from "Pages/Auth/SecurityQuestions/SecurityQuestionsUserName";
import SignatureRequest from "Pages/Auth/SignatureRequest";
import VerifyResult from "Pages/Auth/VerifyResult";
import BatchControlsPage from "Pages/BatchControls";
import BatchReagentsPage from "Pages/BatchReagents";
import BloodResultData from "Pages/Blood/BloodResultData";
import BloodResultDataContextProvider from "Pages/Blood/BloodResultData/BloodResultDataContext";
import BloodLeveyJenning from "Pages/Blood/LeveyJennings";
import PartialRejectedTest from "Pages/Blood/PartialRejectedTest";
import DataContextProvider from "Pages/Blood/PartialRejectedTest/DataContext";
import DynamicGridProvider from "Pages/DynamicGrid/Context/useDynamicGrid";
import ManageFacility from "Pages/Facility/ManageFacility";
import ManageFacilityContextProvider from "Pages/Facility/ManageFacility/FacilityListContext/useManageFacility";
import ViewSingleFacilityDynamic from "Pages/Facility/ViewSingleFacilityDynamic";
import InstrumentPage from "Pages/Instrument";
import CreateExtraction from "Pages/LIS/InfectiousDisease/CreateExtraction";
import ResultDataContextProvider from "Pages/LIS/InfectiousDisease/ResultData/Context";
import ResultFileUpload from "Pages/LIS/InfectiousDisease/ResultFileUpload";
import ResultPdfUpload from "Pages/LIS/InfectiousDisease/ResultPdfUpload/ResultFileUpload";
import ToxLeveyJenning from "Pages/LIS/ToxLeveyJenning";
import ManageSalesGroup from "Pages/ManageSalesGroup";
import MarketPlace from "Pages/MarketPlace";
import MicrobiologyPanelMapping from "Pages/MicroBiologyLis/PanelMapping";
import MicrobiologyResultData from "Pages/MicroBiologyLis/ResultData";
import MicrobiologyResultDataContextProvider from "Pages/MicroBiologyLis/ResultData/ResultDataContext";
import NGSCompendiumData from "Pages/NGS/CompendiumData";
import NGSCreateRun from "Pages/NGS/CreateRun";
import NGSResultData from "Pages/NGS/ResultData";
import NGXResultDataContextProvider from "Pages/NGS/ResultData/ResultDataContext";
import NGSViewRun from "Pages/NGS/ViewRun";
import OncologyCompendiumData from "Pages/OncologyLis/CompendiumData/OncologyCompendiumData";
import OncologyResultFileConfiguration from "Pages/OncologyLis/PreConfiguration/ResultFileConfiguration";
import OncologyResultFileUpload from "Pages/OncologyLis/ResultFileUpload/ResultFileUpload";
import Interpretation from "Pages/PGX/Interpretation";
import InterpretationContextProvider from "Pages/PGX/Interpretation/InterpretationContext";
import PGXLisSetting from "Pages/PGX/LisSettings";
import PGXResultFileConfiguration from "Pages/PGX/ResultDataPreconfiguration/TemplateSetting";
import PGXResultUpload from "Pages/PGX/ResultFIleUpload";
import BillingDataProvier from "Pages/Requisition/Billing-v2/useReqContext";
import ViewRequisitionDataProvider from "Pages/Requisition/SingleRequisition/ViewReq/RequisitionContext/useReqContext";
import WorkLogDataProvider from "Pages/Requisition/WorkLogV2/WorkLogContext/useWorkLogContext";
import UpsPickupAndShipment from "Pages/ShippingPickup/PickupAndShipment";
import FacilityInventory from "Pages/SupplyManagement/FacilityInventory";
import ViewRunsPage from "Pages/ViewRuns";
import CourierContextProvider from "Shared/CourierContext";
import OncologyResultDataContextProvider from "Shared/OncologyResultDataContext";
import TaskTrackerContextProvider from "Pages/Admin/TaskTracker/TaskTrackerContext";

// Lazy-loaded components
const SupplyManagementBulkCheckIn = lazy(
  () => import("../Pages/SupplyManagement/BulkCheckIn")
);

const WorkLogV2 = lazy(() => import("Pages/Requisition/WorkLogV2"));

const SupplyManagementBulkCheckOut = lazy(
  () => import("../Pages/SupplyManagement/BulkCheckOut")
);

const ShippingInformation = lazy(
  () =>
    import(
      "../Pages/SupplyManagement/ManageOrder/NewOrders/ShippingInformation"
    )
);
const PaymentSuccess = lazy(() => import("../Pages/Payment/PaymentSuccess"));
const PaymentFailed = lazy(() => import("../Pages/Payment/PaymentFailed"));
const TrainingAidsCategories = lazy(
  () =>
    import(
      "../Pages/TrainingAidMarketing/TrainingAidsCategory/TrainingAidsCategories"
    )
);
const BulletinBoard = lazy(() => import("../Pages/Marketing/BulletinBoard"));
const ManageSales = lazy(() => import("../Pages/ManageSalesRep"));
const PreConfiguration = lazy(
  () => import("../Pages/ShippingPickup/PreConfiguration")
);
const ToxResultFileConfiguration = lazy(
  () =>
    import(
      "../Pages/Requisition/ToxResultFileConfiguration/ResultFileConfiguration"
    )
);

// ================================================= Start Blood Result-Pre-Configuration Menu =================================================
const BloodResultFileConfiguration = lazy(
  () =>
    import(
      "../Pages/Requisition/BloodResultFileConfiguration/ResultFileConfiguration"
    )
);
// ================================================= END Blood Result-Pre-Configuration Menu =================================================

// ================================================= Start Blood Result File Menu =================================================
const BloodResultFileUpload = lazy(
  () => import("../Pages/LIS/BloodResultFileUpload/BloodResultFile")
);
// ================================================= END Blood Result File Menu =================================================

const ViewOrders = lazy(
  () => import("../Pages/SupplyManagement/ManageOrder/ViewOrders")
);

const MissingInsurance = lazy(
  () => import("../Pages/Requisition/MissingInsurance")
);

const ManageOrderOncology = lazy(
  () => import("../Pages/SupplyManagement/ManageOrderOncology")
);
const SupplyTracking = lazy(() => import("../Pages/SupplyManagement/Tracking"));
const OncologyShippingInformation = lazy(
  () => import("../Pages/SupplyManagement/ManageOrderOncology/OrderForm")
);
const OncologyViewOrders = lazy(
  () => import("../Pages/SupplyManagement/ManageOrderOncology/OrderDetail")
);
const LisFunctionViewAllOrders = lazy(
  () => import("../Pages/LISFunction/ViewAllOrders/index")
);
const LisFunctionGrossingQueue = lazy(
  () => import("../Pages/LISFunction/GrossingQueue/index")
);
const LisFunctionEmbeddingQueue = lazy(
  () => import("../Pages/LISFunction/EmbeddingQueue/index")
);
const LisFunctionMicrotomyQueue = lazy(
  () => import("../Pages/LISFunction/MicrotomyQueue/index")
);
const LisFunctionSendOut = lazy(() => import("../Pages/LISFunction/SendOut"));
const LisFunctionCompendium = lazy(
  () => import("../Pages/LISFunction/Compendium/index")
);
const LisFunctionSpecimenSetting = lazy(
  () => import("../Pages/LISFunction/SpecimenSetting/index")
);
const LisFunctionResultTemplate = lazy(
  () => import("../Pages/LISFunction/ResultTemplate/index")
);
// const LisFunctionPathologyReport = lazy(
//   () => import("../Pages/LISFunction/PathologyReport/index")
// );
// const UpsPickupAndShipment = lazy(
//   () => import("../Pages/ShippingPickup/PickupAndShipment")
// );
const ShippingAndSchedule = lazy(
  () => import("../Pages/ShippingPickup/ShippingAndSchedule")
);
const ManageOrder = lazy(() => import("../Pages/SupplyManagement/ManageOrder"));
const AddUserRoles = lazy(() => import("../Pages/Admin/AddUserRoles"));
const AddReferenceLab = lazy(
  () => import("../Pages/Admin/ReferenceLab/AddReferenceLab/AddReferenceLab")
);
const EditReferenceLab = lazy(
  () => import("../Pages/Admin/ReferenceLab/AddReferenceLab/EditReferenceLab")
);
const ViewReferenceLab = lazy(
  () => import("../Pages/Admin/ReferenceLab/ViewReferenceLab/ViewReferenceLab")
);
const UserManagement = lazy(() => import("Pages/Admin/UserManagement"));
const TaskTracker = lazy(() => import("Pages/Admin/TaskTracker"));
const ChangePassword = lazy(() => import("../Pages/Auth/ChangePassword"));
const InitializePassword = lazy(
  () => import("../Pages/Auth/InitializePassword")
);
const Login2 = lazy(() => import("../Pages/Auth/Login2"));
const SamlAcs = lazy(() => import("../Pages/Auth/SamlAcs"));
const ResetPassword = lazy(() => import("../Pages/Auth/ResetPassword"));
const LabSwitching = lazy(() => import("../Pages/Auth/SwitchingLab"));
const Paneltype = lazy(() => import("../Pages/Compendium/Panel Type"));
const Compendium = lazy(() => import("../Pages/Compendium/PanelGroups"));
const PanelSetup = lazy(() => import("../Pages/Compendium/PanelSetup"));
const PanelSetupGrid = lazy(
  () => import("../Pages/Compendium/PanelSetup/PanelSetupGrid")
);
const QCBatchSetup = lazy(
  () => import("../Pages/Compendium/QCBatchSetup/QCBatchSetup")
);
const SpecimenType = lazy(() => import("../Pages/Compendium/SpecimenType"));
const SpecimenTypeAssigment = lazy(
  () => import("../Pages/Compendium/SpecimenTypeAssigment")
);
const TestAssignmentGrid = lazy(
  () => import("../Pages/Compendium/TestAssignmentGrid")
);
const TestSetUp = lazy(() => import("../Pages/Compendium/TestSetUp"));
const TestSetupGrid = lazy(() => import("../Pages/Compendium/TestSetupGrid"));
const TestType = lazy(() => import("../Pages/Compendium/TestType"));
const BloodCompendiumData = lazy(
  () =>
    import("../Pages/CompendiumData/BloodCompendiumData/BloodCompendiumData")
);
const GroupSetup = lazy(() => import("../Pages/CompendiumData/GroupSetup"));
const DocsViewer = lazy(() => import("../Pages/DocsViewer/DocsViewer"));
const DynamicConfiguration = lazy(
  () => import("../Pages/DynamicConfigurationSetup/DynamicConfiguration")
);
const AddFacility = lazy(() => import("../Pages/Facility/AddFacility"));
const AssignReferenceLab = lazy(
  () => import("../Pages/Facility/AssignReferenceLab/AssignReferenceLab")
);
const EditFacility = lazy(() => import("../Pages/Facility/EditFacility"));
const FacilityApprovalrList = lazy(
  () => import("../Pages/Facility/FacilityApproval/FacilityApprovalrList")
);
const ViewSingleFacilityForApproval = lazy(
  () =>
    import("../Pages/Facility/FacilityApproval/ViewSingleFacilityForApproval")
);
const FacilityOption = lazy(
  () => import("../Pages/Facility/FacilityOptions/FacilityOption")
);
const AddFacilityUser = lazy(
  () => import("../Pages/Facility/FacilityUser/CreateFacilityUser")
);
const EditFacilityUser = lazy(
  () =>
    import("../Pages/Facility/FacilityUser/CreateFacilityUser/EditFacilityUser")
);
const ViewAllUsers = lazy(() => import("../Pages/Facility/FacilityUser"));
const View = lazy(
  () => import("../Pages/Facility/FacilityUser/ViewAllUserTab/View")
);
const LabAssignment = lazy(
  () => import("../Pages/Facility/LabAssignment/LabAssignment")
);
const ManageUserRoles = lazy(() => import("../Pages/Facility/ManageUserRole"));
const ViewAssignedFacilities = lazy(
  () => import("../Pages/Facility/ViewAssignedFacilities")
);
const ViewAssignedUsers = lazy(
  () => import("../Pages/Facility/ViewAssignedUsers")
);
// const ViewSingleFacility = lazy(
//   () => import("../Pages/Facility/ViewSingleFacility")
// );
const ViewSingleUser = lazy(() => import("../Pages/Facility/ViewSingleUser"));
const ICD10Assigment = lazy(
  () => import("../Pages/ICD10Assigment/ICD10Assigment")
);
const InsuranceProviderAssignment = lazy(
  () => import("../Pages/Insurance/InsuranceAssigment")
);
const InfectiousDiseaseCompendiumData = lazy(
  () =>
    import(
      "../Pages/LIS/InfectiousDisease/CompendiumData/InfectiousDiseaseCompendiumData"
    )
);
const PanelMapping = lazy(
  () =>
    import(
      "../Pages/LIS/InfectiousDisease/CompendiumData/PanelMapping/PanelMaping"
    )
);
const MicrobiologySetting = lazy(
  () => import("../Pages/MicroBiologyLis/MicrobiologySettings")
);
const MedicationAssignment = lazy(
  () => import("../Pages/LIS/ToxMedicationAssignment/MedicationAssignment")
);
const RequisitionReflexSetup = lazy(
  () => import("../Pages/RequisitionReflexSetup")
);
const PanelCombinationRestriction = lazy(
  () => import("../Pages/PanelCombinationRestriction")
);
const ToxMedication = lazy(
  () => import("../Pages/LIS/ToxMedicationList/ToxMedication")
);
const RequisitionSummary = lazy(
  () => import("../Pages/Requisition/SingleRequisition/RequisitionSummary")
);
const SingleRequisition = lazy(
  () => import("../Pages/Requisition/SingleRequisition")
);
const PhsyicianSignature = lazy(
  () => import("../Pages/Requisition/SingleRequisition/PhsyicianSignature")
);
const ViewSingleRequisition = lazy(
  () => import("../Pages/Requisition/ViewOfRequisition2")
);
const ManageInventory = lazy(
  () => import("../Pages/SupplyManagement/ManageInventory")
);
const ShippingMethod = lazy(
  () => import("../Pages/SupplyManagement/ShippingMethod")
);
const WorkflowStatusMain = lazy(() => import("../Pages/WorkflowStatus"));
const Requisition = lazy(
  () => import("../Pages/Requisition/SingleRequisition/Requisition")
);
const Waiting = lazy(() => import("../Pages/Requisition/WaitingForSignature"));
const ToxicologyCompendiumData = lazy(
  () => import("../Pages/LIS/Toxicology/ToxicologyCompendiumData")
);
const CreateLab = lazy(() => import("../Pages/Lab/CreateLab"));
const LabsList = lazy(() => import("../Pages/Lab/LabsList"));
const MyFavoriteMenu = lazy(() => import("../Pages/Lab/MyFavoriteMenu"));
const ReferenceLab = lazy(() => import("../Pages/Lab/ReferenceLab"));
const EditPatientDemographics = lazy(
  () => import("../Pages/Patient/PatientDemographic/EditPatient")
);
const PatientDemographicsList = lazy(
  () => import("../Pages/Patient/PatientDemographicsList")
);
const ViewPatient = lazy(
  () => import("../Pages/Patient/ViewPatientDemographics")
);
const PatientInsuranceHistory = lazy(
  () => import("../Pages/Patient/PatientInsuranceHistory")
);
const BulkCheckIn = lazy(() => import("../Pages/Requisition/BulkCheckIn"));
const DrugAllergy = lazy(() => import("../Pages/Requisition/DrugAllergies"));
const IDBatchQC = lazy(
  () => import("../Pages/Requisition/IDBatchQC/IDBatchQC")
);
const LeveyJenning = lazy(
  () => import("../Pages/Requisition/LeveyJenning/LeveyJenning")
);
const PendingRequisition = lazy(
  () => import("../Pages/Requisition/PendingRequisition/PendingRequisition")
);
const PrinterSetup = lazy(
  () => import("../Pages/Requisition/PrinterSetup/PrinterSetup")
);
const RequisitionTypeComp = lazy(
  () => import("../Pages/Requisition/RequisitionType")
);

const IDResultData = lazy(
  () => import("../Pages/LIS/InfectiousDisease/ResultData")
);
const OrderView = lazy(
  () => import("../Pages/Requisition/ViewOfOrderRequisition")
);
const ViewReq = lazy(
  () => import("../Pages/Requisition/SingleRequisition/ViewReq")
);
const ViewBillingReq = lazy(
  () => import("../Pages/Requisition/Billing-v2/index")
);
const ResultFileConfiguration = lazy(
  () => import("../Pages/ResultFileConfiguration/ResultFileConfiguration")
);
const Testing = lazy(() => import("../Pages/Requisition/Testing"));
const PageNotFound = lazy(() => import("../Shared/Common/Pages/PageNotFound"));
const ToxicologyResultData = lazy(() => import("../Pages/LIS/ToxResultData"));
const DynamicGrid = lazy(() => import("../Pages/DynamicGrid"));
const ToxicologyConfirmationResultFile = lazy(
  () =>
    import(
      "../Pages/LIS/ToxicologyConfirmationResultFileUpload/ToxConfirmationResultFile"
    )
);
const ManageNotification = lazy(() => import("../Pages/ManageNotification"));
const BulkPatientUpload = lazy(
  () => import("../Pages/Patient/BulkPatientUpload")
);
const IncompleteRequisition = lazy(
  () => import("../Pages/Requisition/IncompleteRequisition")
);
const TemplateSelector = lazy(() => import("../Pages/Requisition/Testing"));
const SalesRepRequest = lazy(
  () => import("../Pages/SalesManagements/SalesRepRequest")
);
const BloodLisCompendium = lazy(
  () => import("../Pages/Blood/BloodCompendium/BloodLisCompendium")
);
const SecurityQuestions = lazy(
  () => import("../Pages/Auth/SecurityQuestions/SecurityQuestions")
);
const ComorbidityAssignment = lazy(
  () => import("../Pages/ComorbiditiesAssignment/index")
);
const MicroBiologyCompendiumData = lazy(
  () => import("../Pages/MicroBiologyLis/CompendiumData/index")
);
const MicroBioLISSetting = lazy(
  () => import("../Pages/MicroBiologyLis/LisSetting")
);
const PGXCompendiumData = lazy(
  () => import("../Pages/PgxLis/CompendiumData/index")
);
const OncologyPlating = lazy(() => import("../Pages/Oncology/Plating"));

const BloodConfiguration = lazy(
  () => import("../Pages/Blood/Configuration/index")
);
const IDConfiguration = lazy(
  () => import("../Pages/LIS/InfectiousDisease/Configuration/index")
);
const PlateSetup = lazy(
  () => import("../Pages/LIS/InfectiousDisease/PlateSetup/index")
);
const IDViewCreateRun = lazy(
  () => import("../Pages/LIS/InfectiousDisease/ViewCreateRun/index")
);
const ToxConfiguration = lazy(
  () => import("../Pages/LIS/Toxicology/Configuration/index")
);
function ViewRequisitionWrapper() {
  return (
    <ViewRequisitionDataProvider>
      <ViewReq />
    </ViewRequisitionDataProvider>
  );
}

export function PartialRejectedTestWrapper() {
  return (
    <DataContextProvider>
      <PartialRejectedTest />
    </DataContextProvider>
  );
}

function OncologyResultDataWrapper() {
  return (
    <OncologyResultDataContextProvider>
      <OncologyResultData />
    </OncologyResultDataContextProvider>
  );
}
export function CourierWrapper() {
  return (
    <CourierContextProvider>
      <UpsPickupAndShipment />
    </CourierContextProvider>
  );
}

function BloodResultDataWrapper() {
  return (
    <BloodResultDataContextProvider>
      <BloodResultData />
    </BloodResultDataContextProvider>
  );
}

function NGSResultDataWrapper() {
  return (
    <NGXResultDataContextProvider>
      <NGSResultData />
    </NGXResultDataContextProvider>
  );
}

function InterpretationDataWrapper() {
  return (
    <InterpretationContextProvider>
      <Interpretation />
    </InterpretationContextProvider>
  );
}

function BillingWrapper() {
  return (
    <BillingDataProvier>
      <ViewBillingReq />
    </BillingDataProvier>
  );
}

function UserListWrapper() {
  return (
    <UserListContextProvider>
      <UserManagement />
    </UserListContextProvider>
  );
}

function TaskTrackerWrapper() {
  return (
    <TaskTrackerContextProvider>
      <TaskTracker />
    </TaskTrackerContextProvider>
  );
}

function WorkLogWrapper() {
  return (
    <WorkLogDataProvider>
      <WorkLogV2 />
    </WorkLogDataProvider>
  );
}

function ManageFacilityWrapper() {
  return (
    <ManageFacilityContextProvider>
      <ManageFacility />
    </ManageFacilityContextProvider>
  );
}

export function MicrobiologyResultDataWrapper() {
  return (
    <MicrobiologyResultDataContextProvider>
      <MicrobiologyResultData />
    </MicrobiologyResultDataContextProvider>
  );
}

export function AuthenticatorFlowWrapper() {
  return <AuthenticatorWrapper />;
}

export function DynamicGridWrapper() {
  return (
    <DynamicGridProvider>
      <DynamicGrid />
    </DynamicGridProvider>
  );
}

export function IDResultDataWrapper() {
  return (
    <ResultDataContextProvider>
      <IDResultData />
    </ResultDataContextProvider>
  );
}

const AuthRoutes = [
  {
    path: "",
    element: MyFavoriteMenu,
  },
  {
    path: "OrderView",
    element: OrderView,
  },
  {
    path: "view-requisition",
    element: ViewRequisitionWrapper,
  },
  {
    path: "Billing",
    element: BillingWrapper,
  },
  {
    path: "panel-mapping",
    element: PanelMapping,
  },
  {
    path: "microbiology-settings",
    element: MicrobiologySetting,
  },
  {
    path: "lab-assignment",
    element: LabAssignment,
  },

  {
    path: "task-tracker",
    element: TaskTrackerWrapper,
  },

  {
    path: "result-file-template",
    element: ResultFileConfiguration,
  },
  {
    path: "tox-medication-list",
    element: ToxMedication,
  },
  {
    path: "testing",
    element: TemplateSelector,
  },
  {
    path: "tox-confirmation-file-upload",
    element: ToxicologyConfirmationResultFile,
  },
  {
    path: "Pending-requisition",
    element: PendingRequisition,
  },
  {
    path: "dynamic-configuration",
    element: DynamicConfiguration,
  },
  {
    path: "Medication-Assignment",
    element: MedicationAssignment,
  },
  {
    path: "requisition-reflex-setup",
    element: RequisitionReflexSetup,
  },
  {
    path: "panel-combination-restriction",
    element: PanelCombinationRestriction,
  },
  {
    path: "tox-result-pre-configuration",
    element: ToxResultFileConfiguration,
  },
  {
    path: "blood-result-pre-configuration",
    element: BloodResultFileConfiguration,
  },
  {
    path: "blood-file-upload",
    element: BloodResultFileUpload,
  },
  {
    path: "*",
    element: MyFavoriteMenu,
  },
  {
    path: "MyFavorites",
    element: MyFavoriteMenu,
  },
  {
    path: "sales-Rep-Request",
    element: SalesRepRequest,
  },
  {
    path: "microbiology-compendium-data",
    element: MicroBiologyCompendiumData,
  },
  {
    path: "manage-sales-group",
    element: ManageSalesGroup,
  },
  // {
  //   path: "home",
  //   element: Home,
  // },
  {
    path: "PageNotFound",
    element: PageNotFound,
  },

  {
    path: "testing",
    element: Testing,
  },
  {
    path: "add-facility-user",
    element: AddFacilityUser,
  },
  {
    path: "edit-facility-user/:id",
    element: EditFacilityUser,
  },
  {
    path: "add-user-roles",
    element: AddUserRoles,
  },
  {
    path: "toxicology-result-data",
    element: ToxicologyResultData,
  },
  {
    path: "labs",
    element: LabsList,
  },
  {
    path: "CreateLab",
    element: AddReferenceLab,
  },
  {
    path: "facilitylist",
    element: ManageFacilityWrapper,
  },
  {
    path: "RequisitionSummary",
    element: RequisitionSummary,
  },
  {
    path: "facilityApproval",
    element: FacilityApprovalrList,
  },
  {
    path: "manageuserrole",
    element: ManageUserRoles,
  },
  {
    path: "user-management",
    element: UserListWrapper,
  },
  {
    path: "addfacility",
    element: AddFacility,
  },
  {
    path: "editfacility/:id",
    element: EditFacility,
  },
  {
    path: "waiting-signature",
    element: Waiting,
  },
  {
    path: "facility-user-list",
    element: ViewAllUsers,
  },

  {
    path: "facility-view/:id",
    element: ViewSingleFacilityDynamic,
  },
  {
    path: "facility-view-approval/:id",
    element: ViewSingleFacilityForApproval,
  },
  {
    path: "view-assigned-users/:id",
    element: ViewAssignedUsers,
  },
  {
    path: "facility-user-view-by-id/:id",
    element: View,
  },
  {
    path: "assign-refrence-lab",
    element: AssignReferenceLab,
  },

  {
    path: "bulk-check-in",
    element: BulkCheckIn,
  },
  {
    path: "requisition",
    element: SingleRequisition,
  },
  {
    path: "requisition-type",
    element: RequisitionTypeComp,
  },
  {
    path: "levey-jenning",
    element: LeveyJenning,
  },
  {
    path: "view",
    element: ViewSingleRequisition,
  },
  {
    path: "insurance-provider-assigment",
    element: InsuranceProviderAssignment,
  },
  {
    path: "ICD-10-code-assigment",
    element: ICD10Assigment,
  },
  {
    path: "patient-demographics-list",
    element: PatientDemographicsList,
  },
  // {
  //   path: "addpatient",
  //   element: AddPatientDemographics,
  // },
  {
    path: "editpatient/:id",
    element: EditPatientDemographics,
  },
  {
    path: "view-patient-demographics-patient/:id",
    element: ViewPatient,
  },
  {
    path: "patient-insurance-history/:id",
    element: PatientInsuranceHistory,
  },
  {
    path: "compendium-data",
    element: BloodCompendiumData,
  },
  {
    path: "group",
    element: Compendium,
  },
  {
    path: "specimen-type",
    element: SpecimenType,
  },
  {
    path: "group-setup",
    element: GroupSetup,
  },
  {
    path: "drug-allergy",
    element: DrugAllergy,
  },
  {
    path: "panel-setup",
    element: PanelSetup,
  },
  {
    path: "test-setUp",
    element: TestSetUp,
  },
  {
    path: "physician",
    element: PhsyicianSignature,
  },
  {
    path: "test-type",
    element: TestType,
  },
  {
    path: "panel-type",
    element: Paneltype,
  },
  {
    path: "panelsetupgrid",
    element: PanelSetupGrid,
  },
  {
    path: "/testsetupgrid",
    element: TestSetupGrid,
  },
  {
    path: "/testassignmentgrid",
    element: TestAssignmentGrid,
  },
  {
    path: "AddReferenceLab",
    element: ReferenceLab,
  },
  {
    path: "bulletin-board",
    element: BulletinBoard,
  },
  {
    path: "addReferenceLab/:id",
    element: ReferenceLab,
  },
  {
    path: "view-assigned-facilities/:id",
    element: ViewAssignedFacilities,
  },

  {
    path: "facility-user-view/:id",
    element: ViewSingleUser,
  },
  {
    path: "add-reference-lab",
    element: CreateLab,
  },
  {
    path: "edit-reference-lab/:id",
    element: EditReferenceLab,
  },
  {
    path: "reference-lab",
    element: ViewReferenceLab,
  },
  {
    path: "ChangePassword/:id",
    element: ChangePassword,
  },
  {
    path: "authenticator-settings",
    element: AuthenticatorFlowWrapper,
  },
  {
    path: "enable-authenticator",
    element: AuthenticatorFlowWrapper,
  },
  {
    path: "verify-authenticator",
    element: AuthenticatorFlowWrapper,
  },
  {
    path: "verify-authenticator-password",
    element: AuthenticatorFlowWrapper,
  },
  {
    path: "disable-authenticator",
    element: AuthenticatorFlowWrapper,
  },
  {
    path: "disable-authenticator-password",
    element: AuthenticatorFlowWrapper,
  },
  {
    path: "disable-authenticator-success",
    element: AuthenticatorFlowWrapper,
  },
  {
    path: "authenticator-setup-success",
    element: AuthenticatorFlowWrapper,
  },
  {
    path: "specimen-type-assigment",
    element: SpecimenTypeAssigment,
  },
  {
    path: "infectious-disease-compendium-data",
    element: InfectiousDiseaseCompendiumData,
  },
  {
    path: "Toxicology-compendium-data",
    element: ToxicologyCompendiumData,
  },
  {
    path: "result-file-upload",
    element: ResultFileUpload,
  },
  {
    path: "create-extraction",
    element: CreateExtraction,
  },
  {
    path: "result-data",
    element: IDResultDataWrapper,
  },
  {
    path: "oncology-result-data",
    element: OncologyResultDataWrapper,
  },
  {
    path: "id-batch-qc",
    element: IDBatchQC,
  },
  {
    path: "OrderView/:id/:orderid",
    element: OrderView,
  },
  {
    path: "requisition/:id",
    element: Requisition,
  },

  {
    path: "missing-insurance",
    element: MissingInsurance,
  },
  {
    path: "printer-setup",
    element: PrinterSetup,
  },
  {
    path: "qc-batch-setup",
    element: QCBatchSetup,
  },
  {
    path: "facility-option",
    element: FacilityOption,
  },
  {
    path: "workflow-status",
    element: WorkflowStatusMain,
  },
  {
    path: "manage-inventory",
    element: ManageInventory,
  },
  {
    path: "manage-order",
    element: ManageOrder,
  },
  {
    path: "shipping-method",
    element: ShippingMethod,
  },
  {
    path: "manage-order-oncology",
    element: ManageOrderOncology,
  },
  {
    path: "supply-management-tracking",
    element: SupplyTracking,
  },
  {
    path: "supply-order",
    element: ShippingInformation,
  },
  {
    path: "payment-success",
    element: PaymentSuccess,
  },
  {
    path: "payment-failed",
    element: PaymentFailed,
  },
  {
    path: "supply-order-oncology",
    element: OncologyShippingInformation,
  },
  {
    path: "supply-order-view",
    element: ViewOrders,
  },
  {
    path: "supply-order-oncology-view",
    element: OncologyViewOrders,
  },
  {
    path: "Manage-Sales-Rep",
    element: ManageSales,
  },
  {
    path: "specimen-shipped",
    element: ShippingAndSchedule,
  },
  {
    path: "dynamic-grid",
    element: DynamicGrid,
  },
  {
    path: "courier/pre-configuration",
    element: PreConfiguration,
  },
  {
    path: "training-Category",
    element: TrainingAidsCategories,
  },
  {
    path: "incomplete-requisition",
    element: IncompleteRequisition,
  },
  {
    path: "supply-management/bulkcheckin",
    element: SupplyManagementBulkCheckIn,
  },
  {
    path: "supply-management/bulkcheckout",
    element: SupplyManagementBulkCheckOut,
  },
  {
    path: "security/change-questions",
    element: SecurityQuestions,
  },
  {
    path: "bulk-patient-upload",
    element: BulkPatientUpload,
  },
  {
    path: "manage-notification",
    element: ManageNotification,
  },
  {
    path: "blood-compendium",
    element: BloodLisCompendium,
  },
  {
    path: "blood-result-data",
    element: BloodResultDataWrapper,
  },
  {
    path: "work-log",
    element: WorkLogWrapper,
  },
  {
    path: "marketplace",
    element: MarketPlace,
  },
  {
    path: "facilities-inventory",
    element: FacilityInventory,
  },
  {
    path: "result-pdf-upload",
    element: ResultPdfUpload,
  },
  {
    path: "Comorbidity-Assignment",
    element: ComorbidityAssignment,
  },
  {
    path: "microbiology-lis-setting",
    element: MicroBioLISSetting,
  },
  {
    path: "blood-configuration",
    element: BloodConfiguration,
  },
  {
    path: "id-configuration",
    element: IDConfiguration,
  },
  {
    path: "microbiology-panel-mapping",
    element: MicrobiologyPanelMapping,
  },
  {
    path: "partial-rejected-test",
    element: PartialRejectedTestWrapper,
  },
  {
    path: "pgx-compendium-data",
    element: PGXCompendiumData,
  },
  {
    path: "pgx-result-file-template",
    element: PGXResultFileConfiguration,
  },
  {
    path: "pgx-result-file-upload",
    element: PGXResultUpload,
  },
  {
    path: "tox-levey-jenning",
    element: ToxLeveyJenning,
  },
  {
    path: "blood-levey-jenning",
    element: BloodLeveyJenning,
  },
  {
    path: "oncology-pre-config",
    element: OncologyResultFileConfiguration,
  },
  {
    path: "oncology-compendium-data",
    element: OncologyCompendiumData,
  },
  {
    path: "ngs-view-run",
    element: NGSViewRun,
  },
  {
    path: "ngs-create-run",
    element: NGSCreateRun,
  },
  {
    path: "ngs-result-data",
    element: NGSResultDataWrapper,
  },
  {
    path: "pgx-interpretation",
    element: InterpretationDataWrapper,
  },
  {
    path: "pgx-lis-setting",
    element: PGXLisSetting,
  },
  {
    path: "oncology-plating",
    element: OncologyPlating,
  },
  {
    path: "batch-instrument",
    element: InstrumentPage,
  },
  {
    path: "batch-controls",
    element: BatchControlsPage,
  },
  {
    path: "batch-reagent",
    element: BatchReagentsPage,
  },
  {
    path: "view-runs",
    element: ViewRunsPage,
  },
  {
    path: "ngs-compendium-data",
    element: NGSCompendiumData,
  },
  {
    path: "tox-configuration",
    element: ToxConfiguration,
  },
  {
    path: "lis-function/view-all-order",
    element: LisFunctionViewAllOrders,
  },
  {
    path: "lis-function/grossing-queue",
    element: LisFunctionGrossingQueue,
  },
  {
    path: "lis-function/embedding-queue",
    element: LisFunctionEmbeddingQueue,
  },
  {
    path: "lis-function/microtomy-queue",
    element: LisFunctionMicrotomyQueue,
  },
  {
    path: "lis-function/send-out",
    element: LisFunctionSendOut,
  },
  {
    path: "lis-function/compendium",
    element: LisFunctionCompendium,
  },
  {
    path: "lis-function/specimen-setting",
    element: LisFunctionSpecimenSetting,
  },
  {
    path: "lis-function/settings",
    element: LisFunctionResultTemplate,
  },
  // {
  //   path: "lis-function/pathology-report",
  //   element: LisFunctionPathologyReport,
  // },
  {
    path: "platesetup",
    element: PlateSetup,
  },
  {
    path: "id-view-create-run",
    element: IDViewCreateRun,
  },
  {
    path: "oncology-result-file",
    element: OncologyResultFileUpload,
  },
];

//unAuthRoutes
const UnAuthRoutes = [
  {
    path: "/",
    element: Login2,
  },
  {
    path: "/switching",
    element: LabSwitching,
  },
  {
    path: "/InitializePassword",
    element: InitializePassword,
  },
  {
    path: "/ResetPassword",
    element: ResetPassword,
  },
  {
    path: "/login",
    element: Login2,
  },
  {
    path: "/externallogin",
    element: Login2,
  },
  {
    path: "/acs",
    element: SamlAcs,
  },
  {
    path: "/docs-viewer",
    element: DocsViewer,
  },
  {
    path: "/forget-password",
    element: ForgetPassword,
  },
  {
    path: "/signature-request",
    element: SignatureRequest,
  },
  {
    path: "/new-password",
    element: NewPassword,
  },
  {
    path: "/security-questions-username",
    element: SecurityQuestionsUserName,
  },
  {
    path: "/verify/:labId/:userId/:senderType",
    element: VerificationCode,
  },
  {
    path: "/verify-authenticator/:labId/:userId",
    element: AuthenticatorVerificationCode,
  },
  {
    path: "/authentication/:labId/:userId",
    element: AuthenticateWithCode,
  },
  {
    path: "/patient-data-form",
    element: PatientDataForm,
  },
  {
    path: "/verify-result",
    element: VerifyResult,
  },
  {
    path: "/consent-update",
    element: ConsentUpdate,
  },
];

// Getting all routes
const RoutesObj = {
  AuthRoutes,
  UnAuthRoutes,
};

export default RoutesObj;
